// Ported from apps/webapp/src/components/gantt-chart/components/strips-indicators-layer.tsx:110-155
// (`ArrowIndicator`, `Text`, `Name`, `Content`). The old version decided the arrow direction and
// name visibility inside an IntersectionObserver callback by writing `justifySelf`,
// `flexDirection` and an SVG `scaleX` onto the DOM node; here the side is a prop (`offscreenSideOf`
// computes it), so the same two states are plain `[data-side]` rules instead.
.indicator {
  position: sticky;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 20rem;
  max-height: 3.4rem;
  padding: 0.4rem;
  border-radius: var(--gantt-bar-radius);
  color: var(--color-texts-middle-contrast);
  cursor: pointer;
  pointer-events: auto;
  background-color: transparent;
  border: none;

  // strips-indicators-layer.tsx:126-128: the arrow must not shrink below its own size when the
  // date/name text is long enough to want the space.
  & svg {
    min-width: 1.6rem;
  }

  // N1 — STICKY HAS TWO PRECONDITIONS AND BOTH SIDES NEED BOTH. A sticky box is (1) clamped to
  // its containing block — `.track`, which spans the whole timeline width, so the permitted
  // travel is the whole scroll range for either side — and (2) only ever pushed AWAY from the
  // inset's own edge, never pulled towards it. A `left` inset can only push a box RIGHT of where
  // normal flow already put it; a `right` inset can only push it LEFT.
  //
  // Both chips are the only in-flow child of `.track` (`.bar` is absolute), so normal flow puts
  // both at the track's LEFT edge, at canvas x = `--gantt-sidebar-width`.
  //
  // For the START chip that is exactly right: as the user scrolls right, the track's left edge
  // passes behind the sidebar and `left: var(--gantt-sidebar-width)` pushes the chip back to the
  // first visible timeline pixel. Measured: pins at `left: 240` relative to the scrollport at
  // every scroll position past the bar.
  //
  // For the END chip that flow position made the rule INERT. `right: 0` asks "keep this box's
  // right edge at or left of the scrollport's right edge", and a box sitting at the far LEFT of
  // the content already satisfies that at every scroll position — so nothing ever shifted it and
  // it simply scrolled away with the track's origin. Measured before this fix, with the bar off
  // to the right: `left: -8448` relative to the scrollport, i.e. the whole feature was dead on
  // that side while `offscreenSideOf` correctly said "end".
  //
  // The fix is a flow position, not a stronger inset: `margin-inline-start: auto` starts the chip
  // flush against the track's RIGHT edge, which is where `right: 0` finally has something to pull
  // it back from.
  //
  // N5 — WHAT `width: fit-content` IS AND IS NOT DOING, MEASURED. An earlier version of this
  // comment called it load-bearing, on the argument that the auto margin needs a definite width to
  // absorb because a block-level box with `width: auto` resolves auto margins to 0. That argument
  // is false HERE, and the reason is that this box is a `<button>` (`offscreen-indicator.tsx`) —
  // a form control, whose `width: auto` already resolves to shrink-to-fit under UA rules even at
  // `display: flex`. Forced in isolation in Chromium on the real chart, bar scrolled off to the
  // right, scrollport right edge 1280:
  //
  //   fit-content + margin auto   left=1103  right=1280   used width 177.125  ✅ pinned
  //   width:auto  + margin auto   left=1103  right=1280   used width 177.125  ✅ pinned
  //   width:auto  + margin 0      left=-7244                                  ❌ broken
  //   fit-content + margin 0      left=-7244                                  ❌ broken
  //
  // The margin is the entire fix; the width changes nothing. It STAYS, as a declaration rather
  // than as a rationale: "the auto margin works because the UA shrink-to-fits form controls" has
  // been measured in exactly one engine (only Chromium is installed here, and the `gantt-harness`
  // Playwright project is Chromium-only), while this chart ships to Firefox and WebKit too.
  // Declaring the sizing we depend on costs nothing and does not rest on that quirk. If this chip
  // ever stops being a form control, the auto margin keeps working for the same reason it works
  // now — because of this line, not in spite of it.
  &[data-side='start'] {
    left: var(--gantt-sidebar-width);
    flex-direction: row;
  }

  &[data-side='end'] {
    right: 0;
    width: fit-content;
    margin-inline-start: auto;
    flex-direction: row-reverse;

    & svg {
      transform: scaleX(-1);
    }
  }

  &:hover {
    background-color: var(--color-surfaces-bg-elevation-4);

    // strips-indicators-layer.tsx:132-136: brightens the date label on hover. `.name` is already
    // high-contrast unconditionally (matching the old `Name` styled-component), so this is a
    // no-op there and only visibly affects the date-range text.
    .text {
      color: var(--color-texts-high-contrast);
    }
  }
}

.content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.text {
  font-size: 1.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.name {
  color: var(--color-texts-high-contrast);
}
